import { call, put, takeLatest, select, all, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { operationsApi, Operation, CreateOperationRequest, UpdateOperationRequest } from '../../services/operationsApi';
import {
  // Operations actions
  fetchOperationsStart,
  fetchOperationsSuccess,
  fetchOperationsFailure,
  setOperationValidation,
  setOperationFilters,
  setOperationPagination,
  setOperationStatistics,

  // Dropdown actions
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,

  // UI actions
  setOperationDialogOpen,
} from '../reducers/operationsSlice';
import { RootState } from '../index';

// Operations Sagas
function* fetchOperationsApi(): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { pagination, filters } = state.operations.operations;
    
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      search: filters.search || undefined,
      active: filters.active,
      operationTypeId: filters.operationTypeId,
      machineId: filters.machineId,
      partId: filters.partId,
    };

    const response = yield call(operationsApi.getAll, params);
    
    yield put(fetchOperationsSuccess({
      operations: response.data,
      total: response.total,
      page: response.page,
    }));

    // Fetch statistics
    const stats = yield call(operationsApi.getStatistics);
    yield put(setOperationStatistics(stats));
  } catch (error: any) {
    yield put(fetchOperationsFailure(error.message || 'Failed to fetch operations'));
  }
}

function* createOperationApi(action: PayloadAction<CreateOperationRequest>): Generator<any, void, any> {
  try {
    // Validate operation data
    const errors = operationsApi.validation.validateOperation(action.payload);
    if (errors.length > 0) {
      const validation = errors.reduce((acc, error, index) => {
        acc[`field_${index}`] = error;
        return acc;
      }, {} as Record<string, string>);
      yield put(setOperationValidation(validation));
      return;
    }

    // Process the form data
    const processedData = operationsApi.validation.validateBeforeSave(action.payload);
    
    const newOperation = yield call(operationsApi.create, processedData as CreateOperationRequest);
    yield put(setOperationDialogOpen(false));
    yield call(fetchOperationsApi); // Refresh the list
  } catch (error: any) {
    yield put(setOperationValidation({ general: error.message || 'Failed to create operation' }));
  }
}

function* updateOperationApi(action: PayloadAction<UpdateOperationRequest>): Generator<any, void, any> {
  try {
    // Validate operation data
    const errors = operationsApi.validation.validateOperation(action.payload);
    if (errors.length > 0) {
      const validation = errors.reduce((acc, error, index) => {
        acc[`field_${index}`] = error;
        return acc;
      }, {} as Record<string, string>);
      yield put(setOperationValidation(validation));
      return;
    }

    // Process the form data
    const processedData = operationsApi.validation.validateBeforeSave(action.payload);

    const updatedOperation = yield call(operationsApi.update, action.payload.Id, processedData as UpdateOperationRequest);
    yield put(setOperationDialogOpen(false));
    yield call(fetchOperationsApi); // Refresh the list
  } catch (error: any) {
    yield put(setOperationValidation({ general: error.message || 'Failed to update operation' }));
  }
}

function* deleteOperationApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(operationsApi.delete, action.payload);
    yield call(fetchOperationsApi); // Refresh the list
  } catch (error: any) {
    yield put(fetchOperationsFailure(error.message || 'Failed to delete operation'));
  }
}

function* bulkActivateOperationsApi(action: PayloadAction<string[]>): Generator<any, void, any> {
  try {
    yield call(operationsApi.bulkActivate, action.payload);
    yield call(fetchOperationsApi); // Refresh the list
  } catch (error: any) {
    yield put(fetchOperationsFailure(error.message || 'Failed to activate operations'));
  }
}

function* bulkDeactivateOperationsApi(action: PayloadAction<string[]>): Generator<any, void, any> {
  try {
    yield call(operationsApi.bulkDeactivate, action.payload);
    yield call(fetchOperationsApi); // Refresh the list
  } catch (error: any) {
    yield put(fetchOperationsFailure(error.message || 'Failed to deactivate operations'));
  }
}

function* exportOperationsApi(): Generator<any, void, any> {
  try {
    const blob = yield call(operationsApi.export);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `operations-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    yield put(fetchOperationsFailure(error.message || 'Failed to export operations'));
  }
}

// Dropdown Sagas
function* fetchDropdownsApi(): Generator<any, void, any> {
  try {
    const [operationTypes, machines, parts, toolFixtures, workInstructions, inputs] = yield all([
      call(operationsApi.getOperationTypes),
      call(operationsApi.getMachines),
      call(operationsApi.getParts),
      call(operationsApi.getToolFixtures),
      call(operationsApi.getWorkInstructions),
      call(operationsApi.getInputs),
    ]);

    // Process parts data to match Angular format
    const processedParts = parts.map((part: any) => ({
      ...part,
      Name: part.Number, // Use Number as Name for display
    }));

    // Process tool fixtures to match Angular format
    const processedToolFixtures = toolFixtures.map((tf: any) => ({
      ...tf,
      Name: `${tf.Description} - ${tf.TFNum}`,
    }));

    // Process work instructions to match Angular format
    const processedWorkInstructions = workInstructions.map((wi: any) => ({
      ...wi,
      Name: `${wi.Description} - ${wi.Name}`,
    }));

    yield put(fetchDropdownsSuccess({
      operationTypes,
      machines,
      parts: processedParts,
      toolFixtures: processedToolFixtures,
      workInstructions: processedWorkInstructions,
      inputs,
    }));
  } catch (error: any) {
    yield put(fetchDropdownsFailure(error.message || 'Failed to fetch dropdown data'));
  }
}

// Watcher Sagas
function* watchOperationFilters(): Generator<any, void, any> {
  yield takeLatest(setOperationFilters.type, function* () {
    yield call(fetchOperationsApi);
  });
}

function* watchOperationPagination(): Generator<any, void, any> {
  yield takeLatest(setOperationPagination.type, function* () {
    yield call(fetchOperationsApi);
  });
}

// Root Operations Saga
export function* operationsSaga(): Generator<any, void, any> {
  yield all([
    // Initialization
    takeLatest('operations/fetchOperations', fetchOperationsApi),
    takeLatest('operations/fetchDropdowns', fetchDropdownsApi),

    // Operations operations
    takeEvery('operations/createOperation', createOperationApi),
    takeEvery('operations/updateOperation', updateOperationApi),
    takeEvery('operations/deleteOperation', deleteOperationApi),
    takeEvery('operations/bulkActivateOperations', bulkActivateOperationsApi),
    takeEvery('operations/bulkDeactivateOperations', bulkDeactivateOperationsApi),
    takeEvery('operations/exportOperations', exportOperationsApi),

    // Watchers for automatic refetching
    watchOperationFilters(),
    watchOperationPagination(),
  ]);
} 